// src/lib/plain-words.ts
//
// ONE PLACE where a machine code becomes the words an officer reads. A screen never prints a stored role,
// event, state or reference code: it calls `plainCode` (or `roleWords` for a role). The raw code may remain
// only in a tooltip (`title`) or in an audit detail panel labelled as the technical record.
//
// plain-wording-census.test.ts keeps clause numbers, question numbers, table names and raw codes out of copy.

import { ROLE_LABELS } from '@prs2/auth'

/** Words that are not a plain "Capital first, rest lower-case" rendering of the token. */
const ACRONYMS: Readonly<Record<string, string>> = {
  mso: 'MSO',
  hsp: 'HSP',
  ecs: 'eCS',
  mbim: 'MBIM',
  psd: 'PSD',
  cpw: 'CPW',
  faw: 'FAW',
  cwm: 'CWM',
  ch: 'CH',
  hr: 'HR',
  id: 'ID',
  ra: 'RA',
  pa: 'PA',
  cam: 'CAM',
  mmba: 'MMBA',
  prs: 'PRS',
  adfs: 'ADFS',
  wog: 'WOG',
  sgd: 'SGD',
  l1: 'level 1',
  l2: 'level 2',
}

/** Codes whose plain wording is not a mechanical rewrite of the token. */
const SPECIAL: Readonly<Record<string, string>> = {
  AUDIT_EVENT: 'Audit trail entries',
  AUDIT_ARCHIVE_SEGMENT: 'Archived audit files',
  BILL_TRANSITION: 'Bill status history',
  INBOUND_FILE: 'Files received',
  OUTBOUND_FILE: 'Files sent',
  NOTIFICATION_LOG: 'Notification log',
  ARCHIVE_THEN_RETAIN: 'Archive, then keep',
  NO_RULE_IN_FORCE: 'No rule in force on that date',
  WOG_ADFS: 'Whole-of-government sign-in (ADFS)',
  DEMO_ROSTER: 'Demonstration roster',
  SINGPASS_2FA: 'Singpass with two-step verification',
  CORPPASS_2FA: 'Corppass with two-step verification',
  ECS_CARD: 'eCS card',
  FOREIGN_ID: 'Foreign ID',
  UPWARD_ADJUSTMENT: 'Upward adjustment',
  DOWNWARD_ADJUSTMENT: 'Downward adjustment',
  NEW_BILL: 'New bill',
  AWAITING_L1: 'Awaiting level 1 approval',
  AWAITING_L2: 'Awaiting level 2 approval',
  APPROVE_L1: 'Approve at level 1',
  APPROVE_L2: 'Approve at level 2',
  COMPUTE_RECOVERY: 'Work out a recovery',
  EMIT_SETTLEMENT: 'Send a settlement file',
  INGEST_ADJUSTMENT: 'Receive an adjustment',
  INGEST_BILL: 'Receive a bill',
  REQUEST_INFO: 'Ask for more information',
  APPLY_PAYMENT_STATUS: 'Record a payment result',
  NOTHING_SELECTED: 'Nothing selected',
  WARD_RANK_ORDER: 'the system',
  COMPUTE: 'Work out a bill',
  READ: 'View a bill',
  REFER: 'Refer a bill',
  REJECT: 'Reject a bill',
  RESUME: 'Resume a paused bill',
  'demo-stub': 'Demonstration sign-in',
}

/** "FIRST_CONSULTATION" -> "First consultation"; acronyms keep their case. */
export function humaniseCode(code: string): string {
  const words = code.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase().split(/[_\s]+/).filter(Boolean).map((w) => ACRONYMS[w] ?? w)
  if (words.length === 0) return code
  const joined = words.join(' ')
  return joined.charAt(0).toUpperCase() + joined.slice(1)
}

/** The words an officer reads for a stored code. A code nothing knows is humanised, never hidden. */
export function plainCode(code: string): string {
  return SPECIAL[code] ?? humaniseCode(code)
}

/** A role as the officer reads it ("PSD_USER" -> "Public Service Division"), one lookup for the whole app. */
export function roleWords(role: string): string {
  return (ROLE_LABELS as Readonly<Record<string, string>>)[role] ?? humaniseCode(role.replace(/_USER$/, ''))
}

/** "GET /api/bills" -> "Viewed bills". The request line becomes what the officer did. Unknown lines say so plainly. */
export function actionWords(action: string): string {
  const m = /^(GET|POST|PUT|PATCH|DELETE|HEAD)\s+\/api\/([^\s?]*)/.exec(action)
  if (m === null) return /^[A-Z0-9_]+$/.test(action) ? plainCode(action) : action
  const verb = m[1] === 'GET' || m[1] === 'HEAD' ? 'Viewed' : m[1] === 'DELETE' ? 'Removed' : m[1] === 'POST' ? 'Submitted' : 'Changed'
  const parts = (m[2] ?? '').split('/').filter((p) => p !== '' && !p.startsWith(':'))
  const subject = parts.filter((p) => !/^[0-9a-f-]{8,}$/i.test(p)).join(' ').replace(/-/g, ' ')
  if (subject === 'session') return m[1] === 'POST' ? 'Signed in' : 'Viewed session'
  return `${verb} ${subject === '' ? 'a record' : subject}`
}

/**
 * Narrative the server sends (a policy note, a description) was written for developers and cites the tender, a
 * finding, a table. This keeps the sentence and drops the citation, so an officer reads plain PSD wording. It never
 * invents text: it only removes references and renames the third party to PSD.
 */
export function plainProse(text: string, keepIdentifiers = false): string {
  const out = text
    .replace(/\s*\((?:[^()]*?)(?:P2C\d+|Annex A|§|SECURITY\.md|\bQ-\d+|\bS-\d{3}|\bA-\d+)[^()]*\)/g, '')
    .replace(/\b[Ss]tor(?:y|ies) \d+(?:(?:,| and|-) ?\d+)*/g, (m) => (m.startsWith('S') ? 'The requirement' : 'the requirement'))
    .replace(/\bA-\d+\b/g, 'a recorded assumption')
    .replace(/\bat \d{10,} cents\b/g, 'at no monetary limit')
    .replace(/\b(\d[\d,]*) cents\b/g, (_m, n: string) => `SGD ${(Number(n.replace(/,/g, '')) / 100).toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
    .replace(/\bbill type ALL\b/g, 'every bill type')
    .replace(/(?:\bP2C\d+\s*)?(?:§\s*[0-9.]+(?:\([a-z0-9]+\))*\s*[-/]\s*)?§\s*[\d.]+(?:\([a-z0-9]+\))*(?:\s*\/\s*\(?[a-z0-9]+\)?)*/g, 'the requirement')
    .replace(/\bP2C\d+\b/g, 'the requirement')
    .replace(/\bSECURITY\.md\s*S-\d{3}\b|\bS-\d{3}\b/g, 'a recorded finding')
    .replace(/\bQ-\d+\b/g, 'an open question')
    .replace(/\bAnnex A(?: stor(?:y|ies) [\d, and-]+| \d+)?/g, 'the requirements')
    .replace(/\bapp\.[a-z]+(?:_[a-z]+)*(?:\.[a-z_]+)?/g, 'the record')
    .replace(/\bthe Authority\b/g, 'PSD')
    .replace(/\b(?:the )?tender\b/gi, 'the requirements')
    .replace(/demo-stub/g, 'demonstration')
    .replace(/\bstub\b/gi, 'demonstration')
    .replace(/\b\d{3}_[a-z_]+\.sql\b(?: \(and its siblings\)?)?/g, 'the database rules')
    .replace(/\bauthorise\(\)/g, 'the authorisation check')
    .replace(/\b(?:GET|POST|PUT|PATCH|DELETE) \/api\/[^\s,;)]*/g, (m) => actionWords(m).toLowerCase())
  // an officer-typed identifier (a key, a code) inside a message must come back exactly as typed, so the confirmation and
  // the form result keep every upper-case token; every other screen translates a stored code to its words
  if (keepIdentifiers) return out
  return out
    .replace(/\b(ALLOWED|DENIED)\b/g, (m) => m.toLowerCase())
    // a single-word action code only inside a list ("(A, COMPUTE, B)"), so READ-ONLY and the like stay as written
    .replace(/(?<=[(,]\s*)(?:COMPUTE|READ|REFER|REJECT|RESUME)(?=\s*[,)])/g, (m) => plainCode(m))
    .replace(/\b[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\b/g, (m) => plainCode(m))
}

/** The words for the rule definition's own keys, for a refusal that repeats one. */
const RULE_KEY_WORDS: Readonly<Record<string, string>> = {
  officerBasisPoints: 'officer share',
  governmentBasisPoints: 'Government share',
  remainderTo: 'remainder',
  requiredInputs: 'required inputs',
  schemaVersion: 'schema version',
}

/**
 * One refusal of a rule definition, as one plain sentence: the path it begins with becomes where the officer sees it
 * ("table[0].shares[1]: ..." becomes "Row 1, share 2: ..."), the wrapper the route adds is dropped, and an internal key
 * becomes its officer word. The route's message is kept, never replaced.
 */
export function plainRuleProblem(message: string): string {
  let text = message.replace(/^The rule definition is not valid:\s*/i, '')
  // the route now words the row itself ("Row 1: the id may ..."); a path in front of that is dropped, never repeated
  text = text.replace(/^(?:table|applicability|shares)\[\d+\][^:]*:\s*(?=(?:Row|Variant) \d+)/, '')
  text = text.replace(/\(unrecognised field\)/g, 'an unrecognised field')
  text = text.replace(/^((?:table|applicability|shares)\[\d+\](?:\.(?:shares|when)\[\d+\])*)\s*:\s*/, (_m, path: string) => {
    const parts = [...path.matchAll(/(table|applicability|shares|when)\[(\d+)\]/g)].map((p) => {
      const n = Number(p[2]) + 1
      return p[1] === 'table' ? `Row ${n}` : p[1] === 'applicability' ? `Variant ${n}` : p[1] === 'shares' ? `share ${n}` : `condition ${n}`
    })
    return `${parts.join(', ')}: `
  })
  text = text.replace(/\b(?:table|applicability)\[(\d+)\]/g, (m, n: string) => (m.startsWith('table') ? `row ${Number(n) + 1}` : `variant ${Number(n) + 1}`))
  text = text.replace(/\b(officerBasisPoints|governmentBasisPoints|remainderTo|requiredInputs|schemaVersion)\b/g, (k) => RULE_KEY_WORDS[k] ?? k)
  text = text.replace(/^(Row \d+(?:, [a-z]+ \d+)*): (id|label)\b/, '$1: the $2')
  return text.charAt(0).toUpperCase() + text.slice(1)
}
